import { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, Phone, Mail, Calendar as CalIcon, Plus, Trash2, Save,
  Clock, PhoneCall, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "@/lib/apiClient";
import { api } from "@/lib/api";
import CallbackModal from "@/components/CallbackModal";
import { callbackTone, fmtDateTime as fmtCbDateTime } from "@/lib/callbacks";
import { getAgencyOperationalBadge, getAiBadge } from "@/lib/agencyBadges";
import QuickAppointmentModal from "@/components/QuickAppointmentModal";
import { CallButton } from "@/context/VoiceContext";
import { AiStatusControl } from "@/components/agency/AiStatusControl";

function apiErr(e, fallback = "Errore") {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" · ") || fallback;
  if (typeof d === "object") return d.msg || fallback;
  return fallback;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

const CALL_OUTCOME_LABEL = { booked: "Appuntamento", callback_again: "Da richiamare", no_answer: "Nessuna risposta", not_interested: "Non interessato", other: "Altro" };
const CALL_OUTCOME_CLS = { booked: "bg-sky-100 text-sky-700 border-sky-200", callback_again: "bg-purple-100 text-purple-700 border-purple-200", no_answer: "bg-rose-100 text-rose-700 border-rose-200", not_interested: "bg-gray-100 text-gray-600 border-gray-200", other: "bg-gray-100 text-gray-600 border-gray-200" };
const PROVIDER_STATUS_LABEL = { completed: "Completata", busy: "Occupato", "no-answer": "Nessuna risposta", failed: "Fallita", canceled: "Annullata", initiated: "In corso", ringing: "Squilla", "in-progress": "In corso" };
function fmtDuration(sec) {
  if (sec === null || sec === undefined) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function Badge({ badge }) {
  if (!badge) return null;
  const { label, Icon, cls } = badge;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${cls}`}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export default function ContactDrawer({ clientId, tenant, caseObj, onClose, onUpdated }) {
  const api = useApiClient();
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState({ appointments: [] });
  const [activities, setActivities] = useState([]);
  const recap = activities.find((a) => a.type === "conversation_recap");
  const [stages, setStages] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({ name: "", phone: "", email: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [showAppt, setShowAppt] = useState(false);
  const [callbacks, setCallbacks] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [showCallback, setShowCallback] = useState(false);
  const [lifecycleDone, setLifecycleDone] = useState(null);

  const loadCallbacks = useCallback(async () => {
    try {
      const r = await api.get(`/agency/callbacks?client_id=${clientId}`);
      setCallbacks(r.data || []);
    } catch { setCallbacks([]); }
  }, [clientId]);

  const loadCallLogs = useCallback(async () => {
    try {
      const r = await api.get(`/agency/voice/call-logs?client_id=${clientId}`);
      setCallLogs(r.data || []);
    } catch { setCallLogs([]); }
  }, [clientId]);

  const load = useCallback(async () => {
    setLoading(true);
    setLifecycleDone(null);
    try {
      const [c, h, acts, stg, sv, st] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/history`).catch(() => ({ data: { appointments: [] } })),
        api.get(`/clients/${clientId}/activities?limit=100`).catch(() => ({ data: { items: [] } })),
        api.get("/pipeline/stages").catch(() => ({ data: [] })),
        api.get("/services").catch(() => ({ data: [] })),
        api.get("/staff").catch(() => ({ data: [] })),
      ]);
      setClient(c.data);
      setEdit({ name: c.data.name || "", phone: c.data.phone || "", email: c.data.email || "" });
      setHistory(h.data || { appointments: [] });
      setActivities(acts.data?.items || []);
      setStages(stg.data || []);
      setServices(sv.data || []);
      setStaff(st.data || []);
    } catch {
      toast.error("Contatto non accessibile");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [api, clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCallbacks(); }, [loadCallbacks]);
  useEffect(() => { loadCallLogs(); }, [loadCallLogs]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const svcName = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s.name])), [services]);
  const staffName = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s.name])), [staff]);

  const now = new Date();
  const upcoming = (history.appointments || [])
    .filter((a) => a.start && new Date(a.start) >= now && a.status !== "cancelled")
    .sort((a, b) => a.start.localeCompare(b.start));
  const past = (history.appointments || [])
    .filter((a) => a.start && new Date(a.start) < now)
    .slice(0, 5);

  const saveContact = async () => {
    setSavingContact(true);
    try {
      const payload = {
        name: edit.name,
        phone: edit.phone || null,
        email: edit.email ? edit.email : null,
      };
      await api.patch(`/clients/${clientId}`, payload);
      setClient((c) => ({ ...c, ...payload }));
      toast.success("Contatto aggiornato");
      onUpdated?.();
    } catch (e) {
      toast.error(apiErr(e, "Errore salvataggio"));
    } finally { setSavingContact(false); }
  };

  const changeStage = async (key) => {
    const prev = client.pipeline_stage;
    setClient((c) => ({ ...c, pipeline_stage: key }));
    try {
      const res = await api.patch(`/pipeline/${clientId}/stage`, { stage: key });
      toast.success("Fase aggiornata");
      if (res.data?._agency_lifecycle?.closed) {
        setLifecycleDone(res.data._agency_lifecycle);
        toast.success("Gestione Salon Up completata — contatto passato al salone");
      }
      onUpdated?.();
    } catch (e) {
      setClient((c) => ({ ...c, pipeline_stage: prev }));
      toast.error(apiErr(e, "Errore cambio fase"));
    }
  };

  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    setSavingNote(true);
    try {
      await api.post(`/clients/${clientId}/activities/manual`, { text: t });
      setNoteText("");
      const acts = await api.get(`/clients/${clientId}/activities?limit=100`);
      setActivities(acts.data?.items || []);
      toast.success("Nota aggiunta");
    } catch { toast.error("Errore aggiunta nota"); }
    finally { setSavingNote(false); }
  };

  const deleteNote = async (aid) => {
    try {
      await api.delete(`/clients/${clientId}/activities/${aid}`);
      setActivities((prev) => prev.filter((a) => a.id !== aid));
    } catch { toast.error("Errore eliminazione"); }
  };

  const onApptSaved = async () => {
    setShowAppt(false);
    const h = await api.get(`/clients/${clientId}/history`).catch(() => ({ data: { appointments: [] } }));
    setHistory(h.data || { appointments: [] });
    const acts = await api.get(`/clients/${clientId}/activities?limit=100`).catch(() => ({ data: { items: [] } }));
    setActivities(acts.data?.items || []);
    onUpdated?.();
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose} data-testid="aw-client-drawer">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[color:var(--sp-border)] px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 data-testid="aw-drawer-name" className="font-display text-2xl font-black truncate">{client?.name || "…"}</h3>
              <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-brand)] mt-0.5">{tenant?.name}</div>
            </div>
            <button onClick={onClose} data-testid="aw-client-drawer-close" className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[color:var(--sp-subtle)] flex-shrink-0"><X className="h-5 w-5" /></button>
          </div>
          {client && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Badge badge={getAgencyOperationalBadge(caseObj)} />
              <Badge badge={getAiBadge(caseObj)} />
            </div>
          )}
          {client && caseObj?.id && caseObj?.ai_status && (
            <div className="mt-3" data-testid="aw-drawer-ai-control">
              <AiStatusControl caseId={caseObj.id} status={caseObj.ai_status} onChanged={() => onUpdated?.()} />
            </div>
          )}
          {/* Azioni principali */}
          <div className="flex gap-2 mt-4">
            <CallButton testid="aw-drawer-call-btn"
              target={{ client_id: clientId, tenant_id: tenant?.id, agency_case_id: caseObj?.id, clientName: client?.name, tenantName: tenant?.name }}
              className="sp-btn-ghost inline-flex items-center gap-2 flex-1 justify-center disabled:opacity-40" />
            <button data-testid="aw-drawer-book-btn" onClick={() => setShowAppt(true)}
              className="sp-btn-primary inline-flex items-center gap-2 flex-1 justify-center">
              <CalIcon className="h-4 w-4" /> Prenota
            </button>
            <button data-testid="aw-drawer-callback-btn" onClick={() => setShowCallback(true)}
              className="sp-btn-ghost inline-flex items-center gap-2 flex-1 justify-center">
              <PhoneCall className="h-4 w-4" /> Programma richiamo
            </button>
          </div>
        </div>

        {loading || !client ? (
          <div className="p-10 text-center text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Meta */}
            {client.created_at && (
              <div className="text-sm"><div className="text-[10px] uppercase tracking-wider text-[color:var(--sp-text-muted)]">Ingresso</div>{fmtDate(client.created_at)}</div>
            )}

            {/* Dati contatto editabili */}
            <section>
              <h4 className="text-sm font-black mb-2">Dati contatto</h4>
              <div className="space-y-2">
                <input data-testid="aw-drawer-name-input" className="sp-input" placeholder="Nome"
                  value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                <div className="relative">
                  <Phone className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
                  <input data-testid="aw-drawer-phone-input" className="sp-input !pl-9" placeholder="Telefono"
                    value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
                </div>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
                  <input data-testid="aw-drawer-email-input" className="sp-input !pl-9" placeholder="Email"
                    value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
                </div>
                <button data-testid="aw-drawer-save-contact" onClick={saveContact} disabled={savingContact}
                  className="sp-btn-ghost inline-flex items-center gap-2 text-sm disabled:opacity-50">
                  <Save className="h-4 w-4" /> {savingContact ? "Salvataggio…" : "Salva dati"}
                </button>
              </div>
            </section>

            {/* Pipeline stage */}
            <section>
              <h4 className="text-sm font-black mb-2">Fase pipeline</h4>
              <select data-testid="aw-drawer-stage-select" className="sp-input"
                value={client.pipeline_stage || ""} onChange={(e) => changeStage(e.target.value)}>
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </section>

            {/* Fase 8.5 — gestione Agency completata al passaggio in "Prenotato" */}
            {lifecycleDone && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-2" data-testid="aw-drawer-lifecycle-done">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-bold">Gestione Salon Up completata</div>
                  <div className="text-xs">Il contatto è passato alla gestione del salone.{lifecycleDone.cancelled_callbacks > 0 ? ` ${lifecycleDone.cancelled_callbacks} richiamo/i futuri annullati.` : ""}</div>
                </div>
              </div>
            )}

            {/* Prossimo richiamo (Fase 7A) */}
            <section data-testid="aw-drawer-callbacks">
              <h4 className="text-sm font-black mb-2 flex items-center gap-1"><PhoneCall className="h-4 w-4" /> Richiami</h4>
              {(() => {
                const now = new Date();
                const next = callbacks
                  .filter((c) => c.status === "scheduled")
                  .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))[0];
                const done = callbacks.filter((c) => c.status === "completed")
                  .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""))
                  .slice(0, 2);
                if (!next && done.length === 0) {
                  return <p className="text-xs text-[color:var(--sp-text-muted)] italic">Nessun richiamo programmato.</p>;
                }
                return (
                  <div className="space-y-2">
                    {next && (
                      <div className="rounded-lg border border-[color:var(--sp-border)] p-2.5 flex items-center gap-2" data-testid="aw-drawer-next-callback">
                        <Clock className="h-4 w-4 text-[color:var(--sp-brand)]" />
                        <div className="text-sm">
                          <div className="font-bold">{fmtCbDateTime(next.scheduled_at)}</div>
                          <div className="text-[10px] text-[color:var(--sp-text-muted)]">Prossimo richiamo · {next.duration_minutes} min</div>
                        </div>
                        <span className={`ml-auto px-2 py-0.5 rounded-full border text-[10px] font-bold ${callbackTone(next, now).cls}`}>{callbackTone(next, now).label}</span>
                      </div>
                    )}
                    {done.map((c) => (
                      <div key={c.id} className="text-xs text-[color:var(--sp-text-muted)] flex justify-between">
                        <span>{fmtCbDateTime(c.scheduled_at)}</span>
                        <span>{c.outcome || "completato"}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </section>

            {/* Cronologia chiamate (Fase 9C.2) */}
            <section data-testid="aw-drawer-call-history">
              <h4 className="text-sm font-black mb-2 flex items-center gap-1"><PhoneCall className="h-4 w-4" /> Cronologia chiamate</h4>
              {callLogs.length === 0 ? (
                <p className="text-xs text-[color:var(--sp-text-muted)] italic">Nessuna chiamata registrata.</p>
              ) : (
                <div className="space-y-2" data-testid="aw-drawer-call-list">
                  {callLogs.map((l) => (
                    <div key={l.id} data-testid={`aw-drawer-call-${l.id}`}
                      className="flex items-center gap-3 rounded-lg border border-[color:var(--sp-border)] p-2.5">
                      <Phone className="h-4 w-4 text-[color:var(--sp-brand)] flex-shrink-0" />
                      <div className="text-sm min-w-0">
                        <div className="font-bold">{fmtDateTime(l.started_at || l.created_at)}</div>
                        <div className="text-[10px] text-[color:var(--sp-text-muted)]">
                          {fmtDuration(l.duration_seconds)}
                          {l.provider_status ? ` · ${PROVIDER_STATUS_LABEL[l.provider_status] || l.provider_status}` : ""}
                        </div>
                      </div>
                      {l.outcome && (
                        <span className={`ml-auto px-2 py-0.5 rounded-full border text-[10px] font-bold flex-shrink-0 ${CALL_OUTCOME_CLS[l.outcome] || CALL_OUTCOME_CLS.other}`}>
                          {CALL_OUTCOME_LABEL[l.outcome] || l.outcome}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Prossimi appuntamenti */}
            <section>
              <h4 className="text-sm font-black mb-2">Prossimi appuntamenti</h4>
              {upcoming.length === 0 ? (
                <p className="text-xs text-[color:var(--sp-text-muted)] italic">Nessun appuntamento in programma.</p>
              ) : (
                <div className="space-y-2" data-testid="aw-drawer-upcoming">
                  {upcoming.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-lg border border-[color:var(--sp-border)] p-2.5">
                      <CalIcon className="h-4 w-4 text-[color:var(--sp-brand)]" />
                      <div className="text-sm min-w-0">
                        <div className="font-bold">{fmtDateTime(a.start)}</div>
                        <div className="text-xs text-[color:var(--sp-text-muted)] truncate">
                          {(a.service_ids || []).map((id) => svcName[id]).filter(Boolean).join(", ") || "Servizio"}
                          {a.staff_id && staffName[a.staff_id] ? ` · ${staffName[a.staff_id]}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {past.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-[color:var(--sp-text-muted)] cursor-pointer">Storico ({past.length})</summary>
                  <div className="space-y-1 mt-2">
                    {past.map((a) => (
                      <div key={a.id} className="text-xs text-[color:var(--sp-text-muted)] flex justify-between">
                        <span>{fmtDateTime(a.start)}</span><span>{a.status}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </section>

            {/* Note e attività (unificate) */}
            <section>
              <h4 className="text-sm font-black mb-2 flex items-center gap-1"><Clock className="h-4 w-4" /> Note e attività</h4>
              <div className="flex gap-2 mb-3">
                <input data-testid="aw-drawer-note-input" className="sp-input flex-1" placeholder="Aggiungi una nota…"
                  value={noteText} onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} />
                <button data-testid="aw-drawer-note-add" onClick={addNote} disabled={savingNote || !noteText.trim()}
                  className="sp-btn-primary inline-flex items-center gap-1 disabled:opacity-50"><Plus className="h-4 w-4" /></button>
              </div>
              {recap && (
                <div className="mb-3 rounded-lg border border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/5 p-3"
                     data-testid="aw-drawer-ai-recap">
                  <div className="text-[11px] font-black uppercase tracking-wide text-[color:var(--sp-brand)] mb-1">Recap AI</div>
                  <div className="text-sm whitespace-pre-wrap">{recap.description}</div>
                  <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">Aggiornato {fmtDateTime(recap.updated_at || recap.created_at)}</div>
                </div>
              )}
              {activities.length === 0 ? (                <p className="text-xs text-[color:var(--sp-text-muted)] italic">Nessuna attività.</p>
              ) : (
                <div className="space-y-2 border-l-2 border-[color:var(--sp-border)] pl-4" data-testid="aw-drawer-timeline">
                  {activities.slice(0, 40).map((a) => (                    <div key={a.id} className="group relative">
                      <span className={`absolute -left-[21px] top-1.5 h-2 w-2 rounded-full ${a.type === "manual" ? "bg-amber-500" : "bg-[color:var(--sp-brand)]"}`} />
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold whitespace-pre-wrap">{a.type === "manual" ? (a.description || "Nota") : (a.title || a.type)}</div>
                          {a.type !== "manual" && a.description && <div className="text-xs text-[color:var(--sp-text-muted)]">{a.description}</div>}
                          <div className="text-[10px] text-[color:var(--sp-text-muted)]">{a.actor_name ? `${a.actor_name} · ` : ""}{fmtDateTime(a.created_at)}</div>
                        </div>
                        {a.type === "manual" && (
                          <button onClick={() => deleteNote(a.id)} data-testid={`aw-drawer-note-del-${a.id}`}
                            className="opacity-0 group-hover:opacity-100 text-[color:var(--sp-danger)] flex-shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {showAppt && (
        <QuickAppointmentModal clientId={clientId} onClose={() => setShowAppt(false)} onSaved={onApptSaved} />
      )}
      {showCallback && (
        <CallbackModal tenant={tenant} clientId={clientId} clientName={client?.name} caseObj={caseObj}
          onClose={() => setShowCallback(false)} onSaved={() => { loadCallbacks(); onUpdated?.(); }} />
      )}
    </div>
  );
}
